import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Zap, ListChecks, LayoutDashboard, LineChart, Bot,
  Check, ChevronDown, ShieldCheck, Loader2,
} from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";
import { createLead } from "@/lib/api";
import { toast } from "sonner";

const VIOLET = "#7C5CFF";
const INK = "#0E0B1E";
const PANEL = "#171331";
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const grotesk = { fontFamily: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif" };

const FEATURES = [
  { icon: Bot, t: "AI Assistant", d: "Drafts, summarizes, and answers questions grounded in your own docs." },
  { icon: Zap, t: "Workflow Automation", d: "No-code triggers and actions that run the busywork for you." },
  { icon: ListChecks, t: "Smart Tasks", d: "Auto-created from chats and emails — nothing slips through." },
  { icon: LayoutDashboard, t: "Team Dashboard", d: "A live view of priorities, status, and workload across the team." },
  { icon: LineChart, t: "Insights", d: "Weekly progress reports that surface wins and blockers." },
  { icon: ShieldCheck, t: "Enterprise Security", d: "SOC 2 Type II, encryption everywhere, SSO on higher plans." },
];
const STEPS = [
  { n: "01", t: "Connect your tools", d: "Link Slack, Gmail, Drive, Notion & more in a few clicks." },
  { n: "02", t: "Let Nexora watch the work", d: "It drafts, summarizes, and turns messages into tasks automatically." },
  { n: "03", t: "Move faster, together", d: "Your team sees priorities in one place and ships more, sooner." },
];
const INTEGRATIONS = ["Slack", "Gmail", "Google Calendar", "Google Drive", "Notion", "GitHub", "Jira", "Zapier"];
const PLANS = [
  { name: "Starter", price: "$0", per: "free forever", feats: ["Up to 3 users", "Core AI assistant", "Basic tasks"], cta: "Start free", hi: false },
  { name: "Team", price: "$12", per: "per user / month", feats: ["Everything in Starter", "Workflow automation", "All integrations", "Team dashboard"], cta: "Start 14-day trial", hi: true },
  { name: "Business", price: "$24", per: "per user / month", feats: ["Everything in Team", "Advanced insights", "Admin controls & SSO", "Priority support"], cta: "Start 14-day trial", hi: false },
];
const FAQS = [
  { q: "Do I need a credit card to try Nexora?", a: "No. The 14-day trial is free and requires no credit card — just a work email." },
  { q: "How long does setup take?", a: "Most teams are up and running in under 15 minutes. Connect your tools, invite your team, done." },
  { q: "Which tools does Nexora integrate with?", a: "Slack, Gmail, Google Calendar & Drive, Notion, GitHub, Jira, and Zapier — with more added regularly." },
  { q: "Is my data secure?", a: "Yes. Nexora is SOC 2 Type II, encrypts data in transit and at rest, and offers SSO on Business and Enterprise." },
];

function EmailCapture() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!/.+@.+\..+/.test(email)) { toast.error("Please enter a valid work email"); return; }
    setBusy(true);
    try {
      await createLead({ name: email.split("@")[0], email, source: "demo-nexora", service: "Nexora trial", message: "Requested a Nexora free trial (demo)." });
      setDone(true); toast.success("You're on the list — check your inbox!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  if (done) return <div data-testid="nexora-email-done" className="mt-8 rounded-xl px-5 py-4 text-sm inline-flex items-center gap-2" style={{ background: "rgba(124,92,255,0.15)", color: "#CFC4FF" }}><Check size={16} /> Thanks! Your trial link is on its way.</div>;
  return (
    <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
      <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Enter your work email" data-testid="nexora-email-input"
        className="flex-1 rounded-xl px-4 py-3.5 text-sm outline-none text-white placeholder:text-white/40" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} />
      <button onClick={submit} disabled={busy} data-testid="nexora-email-submit"
        className="rounded-xl px-6 py-3.5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap" style={{ background: VIOLET }}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : null} Start free trial
      </button>
    </div>
  );
}

function Faq({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.1)" }} data-testid={`saas-faq-${i}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-white">
        {q}<ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: VIOLET }} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-white/65 leading-relaxed">{a}</div>}
    </div>
  );
}

export default function NexoraDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Nexora (Demo) | WebsiteTech.pro"; }, []);
  const openChat = () => window.dispatchEvent(new Event("concierge:open"));

  return (
    <div className="min-h-screen" style={{ background: INK, color: "#EDEBFF", ...body }} data-testid="saas-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: VIOLET }}>WebsiteTech.pro Demo · SaaS Product — view all demos</Link>
      <DemoTopNav testid="saas-nav"
        logo={<div className="flex items-center gap-2"><div className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: VIOLET }}><Sparkles size={15} className="text-white" /></div><span className="text-lg font-bold tracking-tight" style={grotesk}>Nexora</span></div>}
        items={["Product", "How it works", "Integrations", "Pricing", "FAQ"]} ctaLabel="Get started" onCta={openChat}
        theme={{ bg: "rgba(14,11,30,0.85)", text: "#EDEBFF", accent: VIOLET, accentText: "#fff", border: "rgba(255,255,255,0.08)" }} />

      {/* Hero */}
      <header data-testid="saas-hero" className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(124,92,255,0.35), transparent 70%)" }} />
        <div className="absolute top-40 -left-40 h-[400px] w-[400px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(56,189,248,0.18), transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(124,92,255,0.16)", color: "#CFC4FF" }}><Sparkles size={12} /> New · AI workspace for teams</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" style={grotesk}>AI work,<br />without the busywork.</h1>
            <p className="mt-5 text-lg text-white/70 max-w-lg">Nexora drafts, summarizes, and automates the repetitive work — so your team can focus on what actually matters.</p>
            <EmailCapture />
            <p className="mt-3 text-xs text-white/40">14-day free trial · No credit card required</p>
          </motion.div>
          {/* Dashboard mockup */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="saas-dashboard-mock" className="rounded-2xl border p-4 shadow-2xl" style={{ background: PANEL, borderColor: "rgba(255,255,255,0.1)" }}>
            <div className="flex items-center gap-1.5 mb-4"><span className="h-2.5 w-2.5 rounded-full bg-red-400/70" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" /></div>
            <div className="grid grid-cols-3 gap-3">
              {[{ l: "Tasks done", v: "128", c: "#7C5CFF" }, { l: "Automations", v: "24", c: "#38BDF8" }, { l: "Hrs saved", v: "37", c: "#34D399" }].map((k) => (
                <div key={k.l} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)" }}><div className="text-2xl font-bold" style={{ color: k.c }}>{k.v}</div><div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{k.l}</div></div>
              ))}
            </div>
            <div className="mt-3 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
              <div className="flex items-center gap-2 text-xs text-white/60 mb-3"><Bot size={13} style={{ color: VIOLET }} /> Nexora Assistant</div>
              {["Summarized 3 threads into 1 brief", "Created 5 tasks from #product", "Drafted the weekly update"].map((t, i) => (
                <div key={t} className="flex items-center gap-2 py-1.5 text-sm text-white/80"><Check size={14} style={{ color: "#34D399" }} /> {t}</div>
              ))}
            </div>
            <div className="mt-3 h-16 rounded-xl flex items-end gap-1.5 p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
              {[40, 65, 50, 80, 60, 92, 74].map((h, i) => <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i === 5 ? VIOLET : "rgba(124,92,255,0.35)" }} />)}
            </div>
          </motion.div>
        </div>
      </header>

      {/* Logos / social proof */}
      <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 opacity-50 text-sm font-semibold tracking-wide">
        <span className="text-white/40 text-xs uppercase tracking-widest w-full text-center mb-1">Trusted by fast-moving teams</span>
        {["Orbit", "Northwind", "Lumen Labs", "Fieldnote", "Vantage", "Cobalt"].map((c) => <span key={c} style={grotesk}>{c}</span>)}
      </div>

      {/* How it works */}
      <section data-testid="saas-steps" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "#CFC4FF" }}>See Nexora in action</span><h2 className="mt-2 text-3xl md:text-4xl font-bold" style={grotesk}>Up and running in minutes</h2></div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl border p-7" style={{ background: PANEL, borderColor: "rgba(255,255,255,0.1)" }}>
              <div className="text-3xl font-bold" style={{ ...grotesk, color: VIOLET }}>{s.n}</div>
              <h3 className="mt-3 text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section data-testid="saas-features" className="max-w-7xl mx-auto px-6 py-8">
        <div className="text-center mb-12"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "#CFC4FF" }}>Built for teams that move fast</span><h2 className="mt-2 text-3xl md:text-4xl font-bold" style={grotesk}>Everything, in one workspace</h2></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.t} className="rounded-2xl border p-6 hover:-translate-y-0.5 transition-transform" style={{ background: PANEL, borderColor: "rgba(255,255,255,0.1)" }} data-testid={`saas-feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "rgba(124,92,255,0.16)", color: VIOLET }}><f.icon size={20} /></div>
              <h3 className="mt-4 font-semibold text-lg">{f.t}</h3>
              <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Integrations */}
      <section data-testid="saas-integrations" className="max-w-5xl mx-auto px-6 py-16 text-center">
        <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "#CFC4FF" }}>Integrations</span>
        <h2 className="mt-2 text-3xl font-bold" style={grotesk}>Works with your stack</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {INTEGRATIONS.map((i) => <span key={i} className="px-5 py-2.5 rounded-full text-sm border" style={{ borderColor: "rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.03)" }}>{i}</span>)}
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="saas-pricing" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "#CFC4FF" }}>Pricing</span><h2 className="mt-2 text-3xl md:text-4xl font-bold" style={grotesk}>Simple, per-seat pricing</h2></div>
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {PLANS.map((p) => (
            <div key={p.name} data-testid={`saas-plan-${p.name.toLowerCase()}`}
              className="rounded-2xl border p-7 relative" style={{ background: p.hi ? "rgba(124,92,255,0.1)" : PANEL, borderColor: p.hi ? VIOLET : "rgba(255,255,255,0.1)" }}>
              {p.hi && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full text-white" style={{ background: VIOLET }}>Most popular</span>}
              <h3 className="font-semibold text-lg">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold" style={grotesk}>{p.price}</span><span className="text-sm text-white/50 mb-1">/{p.per}</span></div>
              <ul className="mt-5 space-y-2.5">
                {p.feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-white/75"><Check size={15} style={{ color: VIOLET }} /> {f}</li>)}
              </ul>
              <button onClick={openChat} className="mt-6 w-full rounded-xl px-4 py-3 text-sm font-semibold text-white" style={{ background: p.hi ? VIOLET : "rgba(255,255,255,0.08)" }}>{p.cta}</button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="saas-faq" className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: "#CFC4FF" }}>FAQ</span><h2 className="mt-2 text-3xl font-bold" style={grotesk}>Questions, answered</h2></div>
        <div className="space-y-3">{FAQS.map((f, i) => <Faq key={f.q} {...f} i={i} />)}</div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="rounded-3xl p-10 md:p-14 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2A1E5C 0%, #4B2FA8 100%)" }}>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={grotesk}>Ready to lose the busywork?</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">Start free in minutes. Invite your team when you're ready.</p>
          <button onClick={openChat} data-testid="saas-final-cta" className="mt-7 rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2" style={{ background: "#fff", color: "#2A1E5C" }}>Get started with Nexora <ArrowRight size={16} /></button>
        </div>
      </section>

      <footer data-testid="saas-footer" className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex items-center gap-2"><div className="h-6 w-6 rounded-md grid place-items-center" style={{ background: VIOLET }}><Sparkles size={13} className="text-white" /></div><span className="font-bold text-white/80" style={grotesk}>Nexora</span></div>
          <p>AI work, without the busywork.</p>
          <p>© 2026 Nexora, Inc.</p>
        </div>
      </footer>

      <AIConciergeWidget demo="saas" concierge={DEMO_THEMES.saas.concierge} />
    </div>
  );
}
